// Shared helpers for the Engagement Tracker views (Story / List / Map) and their parts: pure functions
// and data, plus one network helper (fetchMetricSeries). Sections:
//
//   THEME          color language + node-type glyphs/labels
//   METRIC SERIES  live composable-metric fetch (cached) + derivation
//   METRIC SHAPE   node metric_snapshot / live-series accessors → bar/stat/popup shapes
//   STORY          narrative-point beat derivation + momentum
//   FORMAT         label/class formatters, node-evidence accessors, filter constants, edges
//   POPUP          metric-beat popover builder

import axios from "axios";

// =============================================================================
// THEME — the color language:
//
//   color    = health       the only saturated colors; three states (ahead / on track / behind).
//   type     = icon         node types read by neutral glyph, not hue.
//   identity = name         initiatives are told apart by name + position, not color.
//   brand    = interaction  one teal accent for selection, links, and the active tab.
//
// Data only (hexes, tints, FontAwesome icon names); each component registers the icons it renders.
// The teal accent also lives as a CSS token (--color-accent / --color-accent-50 in theme.css) for
// `text-accent` / `bg-accent` / `bg-accent-50` / `ring-accent`.
// =============================================================================

// Health triad, mapped to the app design-system status colors. `tint` is the hue mixed ~88% toward
// white, for soft badge / pill / callout backgrounds.
export const HEALTH = {
  ahead:    { key: "ahead",    label: "Ahead",    pace: "ahead of pace", icon: "arrow-trend-up",   hex: "#389A6C", tint: "#E7F3ED" },
  on_track: { key: "on_track", label: "On track", pace: "on pace",       icon: "arrow-right",      hex: "#4573D2", tint: "#E9EEFA" },
  behind:   { key: "behind",   label: "Behind",   pace: "behind pace",   icon: "arrow-trend-down", hex: "#D53D3D", tint: "#FAE8E8" },
};

// Fallback for indeterminate health (e.g. a "mixed" trajectory): grayscale, not a fourth color.
export const NEUTRAL = { hex: "#6b7280", ink: "#374151", tint: "#f3f4f6" };

// Interaction accent (kept in sync with --color-accent in theme.css).
export const ACCENT = { hex: "#0E8C9E", tint: "#E2F1F3" };

// Node type → glyph + label; the glyph carries the type (no type color). Mirrors the backend
// EngagementGraphNode.NodeType enum (initiative / action_item / constraint / topic / practice_fact).
export const TYPE_META = {
  initiative:  { label: "Initiative",  icon: "bullseye" },
  constraint:  { label: "Constraint",  icon: "ban" },
  action_item: { label: "Action item", icon: "square-check" },
  topic:       { label: "Topic",       icon: "comment" },
  practice_fact: { label: "Practice fact", icon: "circle-info" },
};

// Type → glyph / singular label. Components import these rather than re-deriving from TYPE_META, so
// adding a node type means adding one entry above.
export function typeIcon(type) { return (TYPE_META[type] || {}).icon || "circle-info"; }
export function typeLabel(type) { return (TYPE_META[type] || {}).label || (type || "").replace(/_/g, " "); }

// Canonical display order for initiatives, by initiative_key. Diverge (per UAT 2026-07): Provider
// Referrals pinned top; incentives folded into Quality (no separate card); portal usage dropped as a
// card. Vytalize keys keep their clinical order; Curana mirrors its CCLF Monthly Report tab order.
// Keys not listed sort last (stable), so unknown spines still render.
export const INITIATIVE_ORDER = [
  // Diverge (PL thread order) — Provider Referrals first
  "provider_referrals", "quality_performance", "contracting_amendments",
  "pharmacy_interchange", "cdi",
  // Vytalize
  "awv_completion", "visit_coverage", "risk_documentation", "transitions_of_care", "contract_payments",
  // Curana (not metric-grounded — see setup_engagement_tracker_curana.py)
  "quality_gaps", "coding_recapture", "cost_utilization", "attribution_panel",
  "care_transitions", "care_setting_hospice",
];
// Sort initiative nodes by INITIATIVE_ORDER (by initiative_key); stable for unknown/missing keys.
export function sortInitiatives(nodes) {
  const rank = (n) => {
    const i = INITIATIVE_ORDER.indexOf(n && n.initiative_key);
    return i === -1 ? INITIATIVE_ORDER.length : i;
  };
  return [...nodes].sort((a, b) => rank(a) - rank(b));
}

// Parse a value as a LOCAL date. Timezone-less calendar dates ("YYYY-MM-DD" — engagement/observed_at/
// due_date DateFields) otherwise parse as UTC midnight and land on the wrong day west of UTC; anchor them
// to the LOCAL calendar day so every date renders and compares in the viewer's own (system) time. Full
// datetime strings keep the native parse. Returns null for empty input.
export function parseLocalDate(value) {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(value);
}

// Health for an initiative's trajectory, or null when indeterminate (use NEUTRAL).
export function healthFor(trajectory) {
  return HEALTH[trajectory] || null;
}

// Pace badge for an initiative's trajectory: health hues for the three states, neutral for "mixed".
// Returns null when there's no trajectory.
export function trajectoryBadge(trajectory) {
  const h = HEALTH[trajectory];
  if (h) return { label: h.label, icon: h.icon, hex: h.hex, tint: h.tint };
  if (trajectory === "mixed") return { label: "Mixed", icon: "shuffle", hex: NEUTRAL.ink, tint: NEUTRAL.tint };
  return null;
}

// Story-beat impact: the conversation's directional read of how a beat moved its initiative
// (narrative_point.data.initiative_impact). Reuses the health triad (positive green / negative red /
// neutral gray).
export const IMPACT = {
  positive: { key: "positive", label: "Positive", icon: "arrow-trend-up",   hex: "#389A6C", tint: "#E7F3ED" },
  neutral:  { key: "neutral",  label: "Neutral",  icon: "minus",            hex: "#6b7280", tint: "#f3f4f6" },
  negative: { key: "negative", label: "Negative", icon: "arrow-trend-down", hex: "#D53D3D", tint: "#FAE8E8" },
};
export function impactFor(value) {
  return IMPACT[value] || null;
}

// Momentum: a derived summary of an initiative's recent beat impacts (not a stored field, not metric
// pace). A separate axis from trajectory — the conversation trend. Distinct vocabulary (Improving /
// Steady / Declining) from pace (Ahead / On track / Behind) so the two don't read as the same thing.
export const MOMENTUM = {
  up:   { key: "up",   label: "Improving", icon: "arrow-trend-up",   hex: "#389A6C", tint: "#E7F3ED" },
  flat: { key: "flat", label: "Steady",    icon: "arrow-right",      hex: "#6b7280", tint: "#f3f4f6" },
  down: { key: "down", label: "Declining", icon: "arrow-trend-down", hex: "#D53D3D", tint: "#FAE8E8" },
};

// Sentiment — the practice's ATTITUDE on an initiative (how they FEEL), a SEPARATE axis from momentum
// (progress/direction). Reuses the Diverge engagement-indicator vocabulary so we speak one sentiment
// language across the app. Neutral by design: color stays reserved for health, so sentiment reads by
// word + a face glyph (positive 🙂 / mixed 😐 / negative 🙁), never a new hue.
export const SENTIMENT = {
  enthusiasm:   { label: "Enthusiasm",   polarity: "pos" },
  appreciation: { label: "Appreciation", polarity: "pos" },
  humor:        { label: "Humor",        polarity: "pos" },
  concern:      { label: "Concern",      polarity: "neg" },
  confusion:    { label: "Confusion",    polarity: "neg" },
  frustration:  { label: "Frustration",  polarity: "neg" },
  resistance:   { label: "Resistance",   polarity: "neg" },
};
// polarity → neutral face glyph (FontAwesome). The glyph carries the read; the text stays gray.
export const SENTIMENT_FACE = { pos: "face-smile", mixed: "face-meh", neg: "face-frown" };
// Per-beat glyph info (label + polarity face) for a single sentiment tag — the story-timeline row.
export function sentimentTagInfo(tag) {
  const m = SENTIMENT[tag];
  return m ? { label: m.label, icon: SENTIMENT_FACE[m.polarity] } : null;
}

// Edge coloring from the health palette (blocks/causes → behind-red, addresses → ahead-green, rest
// neutral). edgeHex returns a raw hex for the Map's SVG strokes; edgeStyle returns the inline pill
// style (tint bg + hex text) for a relationship badge.
export function edgeHex(edgeType) {
  if (edgeType === "blocks" || edgeType === "causes") return HEALTH.behind.hex;
  if (edgeType === "addresses") return HEALTH.ahead.hex;
  return NEUTRAL.hex;
}

export function edgeStyle(edgeType) {
  if (edgeType === "blocks" || edgeType === "causes") return { background: HEALTH.behind.tint, color: HEALTH.behind.hex };
  if (edgeType === "addresses") return { background: HEALTH.ahead.tint, color: HEALTH.ahead.hex };
  return { background: NEUTRAL.tint, color: NEUTRAL.ink };
}

// Severity — an urgency scale (the one graded color that isn't health). High reuses the behind-red,
// medium is caution amber, low recedes to neutral. Inline tint/text, like the trajectory + edge badges.
export function severityStyle(severity) {
  if (severity === "high") return { background: HEALTH.behind.tint, color: HEALTH.behind.hex };
  if (severity === "medium") return { background: "#FEF3C7", color: "#B45309" };
  return { background: NEUTRAL.tint, color: NEUTRAL.ink };
}

// =============================================================================
// METRIC SERIES — live composable-metric series (same source as Insights' PerformanceTrend: GET the
// metric meta + POST its time-series run), with a session-scoped cache so the focused view's charts
// and stat row share one fetch per {metric, entity, year}.
// =============================================================================

// key -> Promise<{ meta, results }>, persisting for the SPA session (cleared on reload). entity/year
// are in the key so navigating practices never serves the wrong data; precomputed series are stable
// enough that within-session staleness is acceptable.
const _cache = new Map();

const keyFor = (metricId, entityId, year) => `${metricId}|${entityId}|${year}`;

export function fetchMetricSeries(metricId, entityId, year) {
  if (!metricId || !entityId) return Promise.resolve({ meta: null, results: [] });
  const key = keyFor(metricId, entityId, year);
  if (!_cache.has(key)) {
    const p = Promise.all([
      axios.get(`api/impact_analytics/composable-metrics/${metricId}/`),
      axios.post(`api/impact_analytics/composable-metrics/${metricId}/run/`, {
        observation_year: year, time_series: true, group_by_contract: false, entity_id: entityId,
      }),
    ])
      .then(([metaRes, dataRes]) => ({
        meta: metaRes.data,
        results: (dataRes.data && dataRes.data.results) || [],
      }))
      .catch((e) => {
        _cache.delete(key); // don't cache failures — let the next access retry
        console.error("fetchMetricSeries failed", metricId, entityId, year, e);
        return { meta: null, results: [] };
      });
    _cache.set(key, p);
  }
  return _cache.get(key);
}

// ahead / on_track / behind from the Target+Floor bands (same logic as the backend pace_status):
// at/above the month's target = ahead; below the month's floor = behind; in between (or no floor) =
// on_track. null when there's no rate/target. Keep in lockstep with metric.py.
export function paceBand(rate, target, floor) {
  if (rate == null || target == null) return null;
  if (rate >= target) return "ahead";
  if (floor != null && rate < floor) return "behind";
  return "on_track";
}

// One-line read of a point's Target/Floor band (the "why" behind the pace word). `expected` is the
// on-track target by now, `floor` the minimum acceptable. Point shape: { rate, expected, floor }.
export function bandDesc(p) {
  if (!p || p.rate == null || p.expected == null) return "No on-track target set for this metric.";
  if (p.rate >= p.expected) return "Ahead — at or above the target for now.";
  if (p.floor != null && p.rate < p.floor) return "Behind — below the minimum floor.";
  return "On track — above the floor, below the target.";
}

// Current performance year (YTD metrics reset each Jan), date-ascending. monthly_targets /
// monthly_floors are the 12-length on-track / minimum-acceptable curves in display_config; when
// present they give each point's expected-by-now + floor and the year-end target.
export function deriveMetricSeries(meta, results, year) {
  const displayConfig = (meta && meta.display_config) || {};
  const arr12 = (a) => (Array.isArray(a) && a.length === 12 ? a : null);
  const mt = arr12(displayConfig.monthly_targets);
  const mf = arr12(displayConfig.monthly_floors);

  // Collapse rows sharing an observation_date to the latest performance year (observation_dt_year):
  // Diverge stamps every PY with the run date as observation_date, so a single-observation metric can
  // arrive as two same-date rows and the "latest" reading flickers to the stale prior year (e.g. the
  // card briefly showing 0/1 before 1/1). Keep the current year per date — mirrors the backend's
  // latest_per_observation_date; a no-op for a true dated series (one row per date).
  const byDate = new Map();
  for (const r of results || []) {
    if (!r.observation_date) continue;
    const k = String(r.observation_date).slice(0, 10);
    const prev = byDate.get(k);
    if (!prev || (r.observation_dt_year || 0) > (prev.observation_dt_year || 0)) byDate.set(k, r);
  }

  const current = [...byDate.values()]
    .filter((r) => r.observation_date && Number(String(r.observation_date).slice(0, 4)) === year)
    .map((r) => {
      const den = r.denominator, num = r.numerator;
      const rate = den > 0 ? num / den : (r.rate != null ? r.rate : null);
      const month = Number(String(r.observation_date).slice(5, 7));
      const inYear = month >= 1 && month <= 12;
      return {
        date: String(r.observation_date).slice(0, 10), rate, numerator: num, denominator: den,
        expected: mt && inYear ? mt[month - 1] : null,
        floor: mf && inYear ? mf[month - 1] : null,
      };
    })
    .filter((p) => p.rate != null)
    .sort((a, b) => a.date.localeCompare(b.date));

  const last = current.length ? current[current.length - 1] : null;
  const paceKey = last ? paceBand(last.rate, last.expected, last.floor) : null;

  return {
    name: (meta && meta.name) || null,
    displayConfig,
    current,
    hasBand: !!mt, // monthly_targets present → there's an on-track band (expected-by-now + year-end)
    latest: last ? last.rate : null,
    expectedNow: last ? last.expected : null,
    floorNow: last ? last.floor : null,
    yearEndTarget: mt ? mt[11] : null,
    numerator: last ? last.numerator : null,
    denominator: last ? last.denominator : null,
    paceKey,
  };
}

// =============================================================================
// METRIC SHAPE — node-metric accessors. A node carries two metric sources:
//   • metric_snapshot evidence — per-meeting hard data stored on the node.
//   • the live composable-metric series (above), keyed by metric id in a `liveMetric` cache the Story
//     owns. When loaded it supersedes the snapshot so the card/stat row match the chart; until then
//     the latest snapshot renders so nothing blanks.
// =============================================================================

// Dashed floor marker (a "limit" idiom), distinct from the solid by-now / year-end-target ticks.
// Shared by the card bar and its legend.
export const FLOOR_DASH =
  "repeating-linear-gradient(to bottom, #9ca3af 0, #9ca3af 2px, transparent 2px, transparent 4px)";

export const evByType = (node, type) => ((node && node.evidence) || []).filter((e) => e.type === type);
export const engOf = (e) => e.engagement;
export const dispDate = (e) => e.engagement_date || e.observed_at;

// 0–100% clamp for the absolute-scale bars (card + hover).
export function pctOf(x) {
  return x == null ? 0 : Math.max(0, Math.min(100, x * 100));
}

// All metric ids tracked by a node — the LLM's metric_ids when present, else distinct ids seen in the
// snapshot evidence.
export function metricIdsOf(node) {
  if (node && node.metric_ids && node.metric_ids.length) return node.metric_ids;
  return [...new Set(evByType(node, "metric_snapshot").map((e) => e.data && e.data.metric_id).filter((x) => x != null))];
}

export function hasMetrics(node) {
  return metricIdsOf(node).length > 0;
}

// The metric to headline for an initiative: the domain primary (serializer `primary_metric_id`),
// not metric_ids[0] (which can list e.g. AWV #4 ahead of the real primary AWV+ #125).
export function primaryMetricId(node) {
  if (node && node.primary_metric_id != null) return node.primary_metric_id;
  const ids = metricIdsOf(node);
  return ids.length ? ids[0] : null;
}

// Metric ids with the domain-primary first, so the focused view leads with the headline chart.
export function orderedMetricIds(node) {
  const ids = metricIdsOf(node);
  const pid = primaryMetricId(node);
  if (pid == null || !ids.includes(pid)) return ids;
  return [pid, ...ids.filter((id) => id !== pid)];
}

// Latest full metric_snapshot for a given metric (rate/expected/target/pace/floor/numerator/…).
export function latestSnapshot(node, metricId) {
  if (metricId == null) return null;
  let best = null;
  for (const e of evByType(node, "metric_snapshot")) {
    if (!e.data || e.data.metric_id !== metricId || e.data.rate == null) continue;
    const date = dispDate(e);
    if (!best || String(date) > String(best.date)) best = { date, ...e.data };
  }
  return best;
}

// Latest full metric_snapshot on or before `date` (the value as-of a meeting).
export function snapshotAsOf(node, metricId, date) {
  let best = null;
  for (const e of evByType(node, "metric_snapshot")) {
    if (!e.data || e.data.metric_id !== metricId || e.data.rate == null) continue;
    const d = dispDate(e);
    if (d && String(d) <= String(date) && (!best || String(d) > String(best.__d))) best = { __d: d, ...e.data };
  }
  return best;
}

// A metric's current view: live (cached) once loaded, else the latest snapshot so it renders instantly.
// `hasBand` drives the on-track ticks. `liveMetric` is the Story-owned cache map
// { metricId: deriveMetricSeries(...) }.
export function metricView(node, metricId, liveMetric = {}) {
  const live = metricId != null ? liveMetric[metricId] : null;
  if (live && live.latest != null) {
    return {
      metricId, rate: live.latest, expected: live.expectedNow, floor: live.floorNow,
      target: live.yearEndTarget, numerator: live.numerator, denominator: live.denominator,
      hasBand: live.hasBand,
    };
  }
  const s = latestSnapshot(node, metricId);
  if (!s) return null;
  return {
    metricId, rate: s.rate, expected: s.expected, floor: s.floor, target: s.target,
    numerator: s.numerator, denominator: s.denominator, hasBand: s.expected != null,
  };
}

export function primaryMetricView(node, liveMetric = {}) {
  return metricView(node, primaryMetricId(node), liveMetric);
}

// Absolute 0–100% bar descriptor from a metricView. fill = current; dark tick = year-end target;
// light ticks = floor (below the bar) + on-track-by-now (above). The by-now tick is dropped when it
// equals the year-end target (flat-target metrics like TOC). Returns null when there's no rate to plot.
export function barFromView(v) {
  if (!v || v.rate == null) return null;
  const pct = (x) => (x == null ? null : pctOf(x));
  return {
    fillPct: pct(v.rate),
    floorPct: v.floor != null ? pct(v.floor) : null,
    expectedPct: v.hasBand && v.expected != null && v.expected !== v.target ? pct(v.expected) : null,
    targetPct: v.target != null ? pct(v.target) : null,
    floor: v.floor,
    expected: v.expected,
    target: v.target,
  };
}

// Card progress bar, from the initiative's primary metric. Cards share one absolute scale so they're
// comparable at a glance.
export function cardBar(node, liveMetric = {}) {
  return barFromView(primaryMetricView(node, liveMetric));
}

// Which metric UI an initiative's data calls for — keyed on metric SHAPE, not tenant (the FE mirror of
// the backend's two metric sources). Two shapes today:
//   "trend"     — a few curated metrics, each a dense dated series with a seasonal target/floor band
//                 (MetricSource). Rendered as per-metric trend charts; the story is movement over time.
//   "portfolio" — a set of point-in-time rates judged vs a flat P4P bonus threshold (MetricSnapshotSource).
//                 Rendered as ONE picture of the whole measure set; the story is the cross-sectional
//                 distribution (who clears the bonus bar, where the gaps are) — not a trajectory.
// A snapshot is threshold-shaped when it carries `has_hp_bonus`; the trend shape carries an
// expected/floor band instead. ANY threshold-shaped measure routes to portfolio — including a LONE one
// (e.g. Diverge pharmacy): it then gets the same current-vs-threshold gap "lollipop" as the multi-measure
// set, instead of the trend chart's point-in-time text readout. Band-shaped metrics stay on the trend path.
export function metricDisplayMode(node) {
  const ids = metricIdsOf(node);
  if (!ids.length) return "trend";
  let thresholdShaped = 0;
  for (const id of ids) {
    const s = latestSnapshot(node, id);
    if (s && s.has_hp_bonus !== undefined) thresholdShaped += 1;
  }
  return thresholdShaped >= 1 ? "portfolio" : "trend";
}

// The portfolio read of a metric-grounded initiative whose measures are point-in-time rates vs a P4P
// bonus threshold (EntityMetric.target_rate). Each measure carries its current rate (live-then-snapshot),
// its threshold, whether it clears it, and the compliant members still needed to unlock the bonus.
//
// Below-threshold is OPPORTUNITY (every closed gap already pays; clearing the bar unlocks the higher
// bonus rate), NOT a "behind" health state — so the chart never colors a gap red. `measures` is ordered
// by delta to threshold (rate − threshold), best on top. `closest` is the handful nearest to flipping
// (for the popover); `scoredTotal`/`aboveCount` count only bonus-tier measures (a Numerator-Only measure
// with no bonus neither earns nor loses one).
export function metricPortfolio(node, liveMetric = {}, metricNames = {}) {
  const measures = metricIdsOf(node).map((id) => {
    const v = metricView(node, id, liveMetric);
    const s = latestSnapshot(node, id);
    const threshold = s && s.target != null ? s.target : (v && v.target != null ? v.target : null);
    const hasHpBonus = !!(s && s.has_hp_bonus);
    const name = metricNames[id] || `Metric ${id}`;
    if (!v || v.rate == null) {
      return { metricId: id, name, rate: null, threshold, hasHpBonus, hasData: false, above: null, membersToGo: null, gap: null };
    }
    const above = threshold != null ? v.rate >= threshold : null;
    // Members to flip only means something for a measure with a bonus tier (else there's nothing to
    // "unlock" — every closed gap already pays the flat rate).
    const membersToGo = hasHpBonus && threshold != null && v.denominator && above === false
      ? Math.max(0, Math.round(threshold * v.denominator - (v.numerator || 0))) : null;
    return {
      metricId: id, name, rate: v.rate, numerator: v.numerator, denominator: v.denominator,
      threshold, hasHpBonus, above, membersToGo, hasData: true,
      gap: threshold != null ? v.rate - threshold : null,
    };
  });
  const withData = measures.filter((m) => m.hasData);
  const bonus = withData.filter((m) => m.threshold != null && m.hasHpBonus);
  // Ordered by delta to threshold (rate − threshold), best on top; no-threshold metrics sort last.
  const ranked = [...withData].sort((a, b) => {
    if (a.gap == null && b.gap == null) return (b.rate || 0) - (a.rate || 0);
    if (a.gap == null) return 1;
    if (b.gap == null) return -1;
    return b.gap - a.gap;
  });
  const aboveCount = bonus.filter((m) => m.above).length;
  const closest = bonus
    .filter((m) => m.above === false && m.membersToGo != null && m.rate > 0)
    .sort((a, b) => a.membersToGo - b.membersToGo)
    .slice(0, 3);
  return { measures: ranked, scoredTotal: bonus.length, aboveCount, belowCount: bonus.length - aboveCount, closest, hasData: withData.length > 0 };
}

// The meeting date for an engagement (from its quote/narrative evidence).
export function dateForEngagement(node, eng) {
  const ev = ((node && node.evidence) || []).find(
    (e) => e.engagement === eng && (e.type === "transcript_quote" || e.type === "narrative_point"),
  );
  return ev ? dispDate(ev) : null;
}

// Metric values as-of a beat's meeting date — feeds the Story metric popover. `metricNames` is the
// id→name map so each row is labeled.
export function metricBeatData(node, eng, metricNames = {}) {
  const date = dateForEngagement(node, eng);
  if (!date) return [];
  return metricIdsOf(node)
    .map((id) => {
      const s = snapshotAsOf(node, id, date);
      if (!s) return null;
      return {
        name: metricNames[id] || `Metric ${id}`, rate: s.rate, expected: s.expected, pace: s.pace,
        target: s.target, numerator: s.numerator, denominator: s.denominator, rate_efctv: s.rate_efctv,
      };
    })
    .filter(Boolean);
}

// =============================================================================
// STORY — narrative-point beat derivation, shared by Story-all, Story-focused, and List-detail.
// =============================================================================

// Momentum = net of an initiative's most-recent N beat impacts.
export const MOMENTUM_WINDOW = 5;

// A node's story beats: narrative points (date-sorted) + chips/refs/quote/impact. Each beat carries its
// own backing quote (data.quote, validated verbatim server-side), so beat↔quote is 1:1; a purely
// quantitative beat has none. Metric chip whenever the node is metric-grounded. `allNodes` resolves
// node_ref_ids to clickable sibling chips.
export function beatsFor(node, allNodes = []) {
  if (!node) return [];
  const hasMetric = metricIdsOf(node).length > 0;
  const engInfo = {};
  for (const e of node.evidence || []) {
    if (e.engagement != null && !engInfo[e.engagement]) {
      engInfo[e.engagement] = { subject: e.engagement_subject, type: e.engagement_type_name };
    }
  }
  const byId = {};
  for (const n of allNodes) byId[n.id] = n;
  return evByType(node, "narrative_point")
    .map((e) => ({ e, eng: engOf(e), date: dispDate(e) }))
    .sort((a, b) => String(a.date).localeCompare(String(b.date)))
    .map(({ e, eng, date }) => {
      const quote = (e.data && e.data.quote) || "";
      const info = engInfo[eng] || {};
      const chips = [];
      if (hasMetric) chips.push({ kind: "metric", label: "Metric" });
      if (quote) chips.push({ kind: "quote", label: "Engagement quote" });
      const refs = ((e.data && e.data.node_ref_ids) || [])
        .map((id) => byId[id])
        .filter((n) => n && n.id !== node.id)
        .map((n) => ({ id: n.id, title: n.title, type: n.type }));
      return {
        node,
        engagement: eng,
        date,
        text: (e.data && e.data.text) || "",
        chips,
        refs,
        quote,
        quoteSource: quote ? info.subject || info.type || "" : "",
        impact: (e.data && e.data.initiative_impact) || null,
        sentiment: (e.data && e.data.sentiment) || null,
      };
    });
}

// Momentum from a beat list: net of the most-recent impacts — a separate axis from metric pace.
// null when no beat carries an impact.
export function momentumOf(beats) {
  const impacts = (beats || []).map((b) => b.impact).filter(Boolean);
  if (!impacts.length) return null;
  const recent = impacts.slice(-MOMENTUM_WINDOW);
  let pos = 0, neg = 0, neu = 0;
  for (const i of recent) {
    if (i === "positive") pos++;
    else if (i === "negative") neg++;
    else neu++;
  }
  const net = pos - neg;
  const key = net > 0 ? "up" : net < 0 ? "down" : "flat";
  return { ...MOMENTUM[key], pos, neg, neu, n: recent.length };
}

// Sentiment rollup for an initiative: the net ATTITUDE across its recent beats — a separate axis from
// momentum (progress). null when no beat carries a sentiment. Mirrors momentumOf's window + shape.
// Headline label = the most-frequent tag (the vivid word); ties / no clear lead → "Mixed".
export function sentimentOf(beats) {
  const tags = (beats || []).map((b) => b.sentiment).filter((t) => SENTIMENT[t]);
  if (!tags.length) return null;
  const recent = tags.slice(-MOMENTUM_WINDOW);
  const counts = {};
  let pos = 0, neg = 0;
  for (const t of recent) {
    counts[t] = (counts[t] || 0) + 1;
    if (SENTIMENT[t].polarity === "pos") pos++; else neg++;
  }
  const net = pos - neg;
  const polarity = net > 0 ? "pos" : net < 0 ? "neg" : "mixed";
  let domTag = null, domN = 0, tie = false;
  for (const [t, n] of Object.entries(counts)) {
    if (n > domN) { domN = n; domTag = t; tie = false; }
    else if (n === domN) tie = true;
  }
  const label = (!tie && domTag) ? SENTIMENT[domTag].label : "Mixed";
  return { label, polarity, icon: SENTIMENT_FACE[polarity], pos, neg, n: recent.length, counts };
}

// All initiatives' beats merged + date-sorted — the practice-wide story. Each beat is tagged with its
// initiative's health (for the clickable title pill) plus node/nodeId/title.
export function overallBeatsFor(initiatives, allNodes = []) {
  const out = [];
  for (const node of initiatives) {
    const health = healthFor(node.trajectory) || NEUTRAL;
    for (const b of beatsFor(node, allNodes)) {
      out.push({ ...b, node, nodeId: node.id, title: node.title, health });
    }
  }
  return out.sort((a, b) => String(a.date).localeCompare(String(b.date)));
}

// =============================================================================
// FORMAT — label/class formatters, node-evidence accessors, and the filter/group constants.
//   fmt        — value→label/class helpers + node-evidence accessors; spread into a component's
//                `methods` so templates call them bare (statusLabel(x), quotesFor(node), …).
//   edgesFor() — takes the edge list + a title map, called explicitly with that context.
// =============================================================================

// Canonical node order — the SINGLE source for BOTH the List group order and the Map lane order (Map
// derives its lanes from this), so the two views never drift. Plural labels for the filter dropdown +
// List headers; singular labels live in TYPE_META (read via typeLabel).
export const TYPE_GROUPS = [
  { type: "initiative", label: "Initiatives" },
  { type: "action_item", label: "Action Items" },
  { type: "constraint", label: "Constraints" },
  { type: "practice_fact", label: "Practice Facts" },
  { type: "topic", label: "Topics" },
];

export const STATUS_OPTIONS = ["open", "in_progress", "resolved", "dropped"];
const STATUS_LABEL = { open: "Open", in_progress: "In progress", resolved: "Resolved", dropped: "Dropped" };
// Status is neutral (color is reserved for health): distinguished by gray value, not hue — active
// states a touch darker, closed states muted. Mirrors the Map's status shapes (○ open · ◐ in progress
// · ● resolved · dashed dropped).
const STATUS_CLASS = {
  open: "bg-gray-100 text-gray-600",
  in_progress: "bg-gray-200 text-gray-700",
  resolved: "bg-gray-100 text-gray-400",
  dropped: "bg-gray-100 text-gray-400",
};

export const SEVERITY_RANK = { high: 0, medium: 1, low: 2 };
export const SEVERITY_OPTIONS = ["high", "medium", "low"];

// Practice-fact stance — the kind of durable trait (metadata.stance). Neutral by design: it reads by
// word, not hue (color stays reserved for health), so it renders as a gray pill like the status badge.
const STANCE_LABEL = { strength: "Strength", preference: "Preference", constraint: "Constraint", friction: "Friction" };

function humanize(s) { return s ? s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : s; }
function typeMeta(type) {
  const g = TYPE_GROUPS.find((x) => x.type === type);
  return { label: g ? g.label : humanize(type) };
}
function quotesFor(node) {
  return (node.evidence || [])
    .filter((e) => e.type === "transcript_quote")
    .map((e) => ({
      id: e.id, engagement: e.engagement, engagement_date: e.engagement_date,
      engagement_subject: e.engagement_subject, engagement_type_name: e.engagement_type_name,
      quote: (e.data && e.data.quote) || "",
    }));
}
function narrativesFor(node) {
  return (node.evidence || [])
    .filter((e) => e.type === "narrative_point")
    .map((e) => ({ id: e.id, engagement: e.engagement, date: e.observed_at || e.engagement_date, text: (e.data && e.data.text) || "" }))
    .sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));
}
function mentionsCount(node) { return quotesFor(node).length; }
// Most recent date a transcript quote was attached — "last discussed".
function lastDiscussed(node) {
  let max = null;
  for (const q of quotesFor(node)) {
    if (q.engagement_date && (!max || q.engagement_date > max)) max = q.engagement_date;
  }
  return max;
}

export const fmt = {
  humanize, typeMeta, quotesFor, narrativesFor, mentionsCount, lastDiscussed,
  typeLabel,
  statusClass: (s) => STATUS_CLASS[s] || "bg-gray-100 text-gray-500",
  statusLabel: (s) => STATUS_LABEL[s] || s,
  // Status glyph mirroring the Map's SVG shapes (○ open · ◐ in progress · ● resolved · ◌ dropped), so
  // the status badge + <select> options read in the same language.
  statusGlyph: (s) => ({ open: "○", in_progress: "◐", resolved: "●", dropped: "◌" }[s] || "○"),
  confidencePct: (c) => (c == null ? "" : `${Math.round(c * 100)}%`),
  // Stance label for a practice_fact (null for other types / no stance) — read from node.metadata.stance.
  stanceLabel: (node) => {
    const s = node && node.type === "practice_fact" && node.metadata && node.metadata.stance;
    return s ? STANCE_LABEL[s] || humanize(s) : null;
  },
  fmtRate: (v) => (v == null ? "—" : `${(v * 100).toFixed(1)}%`),
  evidenceLabel: (ev) => ev.engagement_subject || ev.engagement_type_name || "",
  ownerLabel: (node) => node.owner_display || null,
  dimmed: (node) => ["resolved", "dropped"].includes(node.status),
  overdue: (node) => {
    if (!node.due_date || ["resolved", "dropped"].includes(node.status)) return false;
    return parseLocalDate(node.due_date) < new Date(new Date().toDateString());
  },
  daysSince: (dateString) => {
    const d = parseLocalDate(dateString);
    return d ? Math.floor((Date.now() - d.getTime()) / 86400000) : Infinity;
  },
  // Dates render in the viewer's own (system) time via parseLocalDate — a bare "YYYY-MM-DD" otherwise
  // parses as UTC midnight and shows a day early west of UTC (Feb 17 -> "Feb 16").
  formatDate: (dateString) => {
    const d = parseLocalDate(dateString);
    return d ? d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";
  },
};

// Relationships of a node, resolved to the other end's title. Carries the edge's own evidence
// (confidence + the verbatim quote/engagement backing a causal link) so the detail row can show a
// muted confidence and unwind the quote like a Story beat.
export function edgesFor(edges, titleById, nodeId) {
  const out = [];
  for (const e of edges) {
    const evidence = {
      confidence: e.confidence,
      evidence_quote: e.evidence_quote,
      evidence_engagement: e.evidence_engagement,
    };
    if (e.from_node === nodeId) {
      out.push({ ...evidence, edge_type: e.edge_type, label: e.edge_type.replace(/_/g, " "), otherTitle: titleById[e.to_node] || e.to_node, otherId: e.to_node });
    } else if (e.to_node === nodeId) {
      out.push({ ...evidence, edge_type: e.edge_type, label: `${e.edge_type.replace(/_/g, " ")} by`, otherTitle: titleById[e.from_node] || e.from_node, otherId: e.from_node });
    }
  }
  return out;
}

// =============================================================================
// POPUP — metric-beat popover builder. A story beat's "Metric" chip opens a popover showing each
// tracked metric's value as-of that meeting. Shared by EngagementTrackerStory and ET_NodeDetail.
// =============================================================================

// Build the { title, hex, date, metrics, left, top } popup descriptor from a beat. `event` is the click
// event whose target anchors the popover. hex = the node's health hue (the popover's accent dot).
export function buildMetricPopup(node, engagement, event, metricNames = {}) {
  const r = event && event.currentTarget ? event.currentTarget.getBoundingClientRect() : { left: 20, bottom: 20 };
  const anyEv = (node.evidence || []).find((e) => engOf(e) === engagement);
  return {
    title: node.title,
    hex: (healthFor(node.trajectory) || NEUTRAL).hex,
    date: anyEv ? dispDate(anyEv) : null,
    metrics: metricBeatData(node, engagement, metricNames),
    left: Math.max(8, Math.min(r.left, window.innerWidth - 296)),
    top: Math.min(r.bottom + 6, window.innerHeight - 230),
  };
}
